import { useCallback, useEffect, useRef, useState } from 'react'

import { useCheckoutApi } from './useCheckoutApi'

import { Checkout, CheckoutStatus } from '../../types/booking/booking'
import { isOpenCheckout } from '../../utils/booking/booking'

/** Fast polling right after returning from the payment page, while the provider confirms the payment. */
const RETURN_POLL_MS = 2_000
const RETURN_FAST_WINDOW_MS = 60_000
const PENDING_POLL_MS = 10_000
const AFTER_DEADLINE_POLL_MS = 3_000
const RECONCILING_POLL_MS = 5_000

/**
 * Loads a checkout by id (recoverable after a refresh) and keeps it current while its outcome is open. The countdown
 * deadline is derived from the server's own clock: remaining = expiresAt - serverTime at the moment of the response,
 * added to the local time the response arrived, so a wrong device clock cannot shift it. Each read lets the server
 * reconcile with the payment provider; the server alone decides the outcome.
 */
export const useCheckoutStatus = (checkoutId?: string | null, returnedFromPayment = false) => {
  const { get, cancel: cancelCheckout } = useCheckoutApi()
  const [checkout, setCheckout] = useState<Checkout | null>(null)
  const [deadline, setDeadline] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [canceling, setCanceling] = useState(false)
  const loadedAt = useRef(Date.now())
  const inFlight = useRef(false)

  const apply = useCallback((result: Checkout | null) => {
    if (!result) {
      return
    }
    setCheckout(result)
    if (result.expiresAt && result.serverTime) {
      const remaining = new Date(result.expiresAt).getTime() - new Date(result.serverTime).getTime()
      setDeadline(Date.now() + remaining)
    } else {
      setDeadline(null)
    }
  }, [])

  const refresh = useCallback(async () => {
    if (!checkoutId || inFlight.current) {
      return
    }
    inFlight.current = true
    try {
      apply(await get(checkoutId))
      setError(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      inFlight.current = false
    }
  }, [apply, checkoutId, get])

  useEffect(() => {
    loadedAt.current = Date.now()
    setCheckout(null)
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutId])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [])

  const remainingMs = deadline === null ? null : Math.max(0, deadline - now)
  const open = isOpenCheckout(checkout?.status)

  useEffect(() => {
    if (!checkout || !open) {
      return
    }
    const sinceLoad = Date.now() - loadedAt.current
    const interval =
      checkout.status === CheckoutStatus.RECONCILING
        ? RECONCILING_POLL_MS
        : remainingMs === 0
          ? AFTER_DEADLINE_POLL_MS
          : returnedFromPayment && sinceLoad < RETURN_FAST_WINDOW_MS
            ? RETURN_POLL_MS
            : PENDING_POLL_MS
    const timer = window.setTimeout(refresh, interval)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout, open, remainingMs === 0, returnedFromPayment, refresh])

  useEffect(() => {
    const onFocus = () => {
      if (open) {
        refresh()
      }
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [open, refresh])

  const cancel = useCallback(async () => {
    if (!checkoutId) {
      return
    }
    setCanceling(true)
    try {
      apply(await cancelCheckout(checkoutId))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setCanceling(false)
    }
  }, [apply, cancelCheckout, checkoutId])

  return { checkout, remainingMs, error, refresh, cancel, canceling: canceling }
}
